<template>
  <div class="calendar-wrapper">
    <button
      v-if="showNavigation"
      @click="$emit('previous')"
      class="nav-button nav-button-prev"
      :disabled="loading"
      :aria-label="'Mois précédent'"
    >
      <span v-if="loading" class="loading-spinner"></span>
      <span v-else>&lt;</span>
    </button>

    <transition-group name="calendar-slide" tag="div" class="calendar-container">
      <div
        v-for="month in months"
        :key="month.key"
        class="month-calendar"
      >
        <h3 class="month-title">{{ month.name }}</h3>
        <div class="days-grid">
          <!-- En-têtes des jours -->
          <div
            v-for="dayName in dayHeaders"
            :key="dayName"
            class="day-header"
          >
            {{ dayName }}
          </div>

          <!-- Jours vides au début du mois -->
          <div
            v-for="n in month.firstDayOfWeek"
            :key="'empty-' + n"
            class="empty-day"
          ></div>

          <!-- Jours du mois -->
          <div
            v-for="day in month.daysInMonth"
            :key="`${month.key}-${day}`"
            class="day"
            :class="getDayClasses(month.year, month.monthIndex, day)"
            @click="handleDayClick(month.year, month.monthIndex, day)"
          >
            <span
              v-if="hasContentOnDate && hasContentOnDate(month.year, month.monthIndex, day)"
              class="day-indicator"
            ></span>
            <span class="day-number">{{ day }}</span>
          </div>
        </div>
      </div>
    </transition-group>

    <button
      v-if="showNavigation"
      @click="$emit('next')"
      class="nav-button nav-button-next"
      :disabled="loading"
      :aria-label="'Mois suivant'"
    >
      <span v-if="loading" class="loading-spinner"></span>
      <span v-else>&gt;</span>
    </button>
  </div>
</template>

<script setup>
const props = defineProps({
  months: {
    type: Array,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  dayHeaders: {
    type: Array,
    default: () => ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
  },
  hasContentOnDate: {
    type: Function,
    default: null
  },
  isDateSelected: {
    type: Function,
    default: null
  },
  enableDayClick: {
    type: Boolean,
    default: true
  },
  showNavigation: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['previous', 'next', 'day-click'])

const getDayClasses = (year, monthIndex, day) => {
  const classes = []
  
  if (props.hasContentOnDate && props.hasContentOnDate(year, monthIndex, day)) {
    classes.push('has-content')
  }
  
  if (props.isDateSelected && props.isDateSelected(year, monthIndex, day)) {
    classes.push('selected')
  }
  
  if (props.enableDayClick && props.hasContentOnDate && props.hasContentOnDate(year, monthIndex, day)) {
    classes.push('clickable')
  }
  
  return classes
}

const handleDayClick = (year, monthIndex, day) => {
  if (!props.enableDayClick) return
  
  if (props.hasContentOnDate && !props.hasContentOnDate(year, monthIndex, day)) {
    return
  }
  
  emit('day-click', { year, monthIndex, day })
}
</script>

<style scoped>
.calendar-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 1rem 0;;
}

.nav-button {
  font-size: 2rem;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--primary);
  padding: 8px 12px;
  border-radius: var(--border-radius);
  transition: all 0.2s ease;
}

.nav-button:hover:not(:disabled) {
  background: var(--primary);
  color: var(--white);
  transform: scale(1.1);
}

.nav-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.calendar-container {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: nowrap;
}

.month-calendar {
  border: 1px solid #ddd;
  border-radius: var(--border-radius);
  padding: 15px;

  min-height: 320px;
  background: var(--white);
  box-shadow: var(--shadow);
}

.month-title {
  text-align: center;
  margin-bottom: 15px;
  color: var(--primary);
  font-weight: 600;
}

.days-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 5px;
}

.day-header {
  text-align: center;
  font-weight: bold;
  padding: 8px 4px;
  color: var(--secondary);
  font-size: 0.875rem;
}

.day {
  text-align: center;
  padding: 8px;
  position: relative;
  border-radius: 4px;
  transition: all 0.2s ease;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.day.clickable {
  cursor: pointer;
}

.day.clickable:hover {
  background: #f0f0f0;
}

.day.has-content .day-indicator {
  position: absolute;
  top: 2px;
  left: 0;
  right: 2px;
  bottom: 2px;
  background: var(--primary);
  opacity: 0.3;
  border-radius: 50%;
  pointer-events: none;
  width: 36px;
  height: 36px;
}

.day.has-content.selected {
  color: white;
}

.day.has-content.selected .day-indicator {
  opacity: 1;
}

.day-number {
  position: relative;
  z-index: 2;
}

.empty-day {
  min-height: 32px;
}

/* Animations */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.calendar-slide-enter-active,
.calendar-slide-leave-active {
  transition: all 0.3s ease;
}

.calendar-slide-enter-from {
  opacity: 0;
  transform: translateX(50px);
}

.calendar-slide-leave-to {
  opacity: 0;
  transform: translateX(-50px);
}

/* Responsive */
@media (max-width: 768px) {
  .calendar-container {
    flex-wrap: wrap;
    min-width: auto;
  }
  

  .nav-button {
    font-size: 1.5rem;
  }
}
</style>
